

import React, { useState, useEffect } from 'react';
import { ref, onValue, off, remove, update } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../firebase';
import SupplierForm from './SupplierForm';
import PayableForm from './PayableForm';
import * as XLSX from 'xlsx';
import { Edit3, Trash2, DollarSign } from 'lucide-react';

const addButtonStyle = {
  padding: '0.875rem 2rem',
  borderRadius: '8px',
  backgroundColor: '#2563eb',
  color: 'white',
  fontWeight: '600',
  cursor: 'pointer',
  border: 'none',
  fontSize: '0.95rem',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
  transform: 'translateY(0)',
};

const SupplierTable = ({ onAddSupplier }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showPayableForm, setShowPayableForm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);

    const suppliersListener = onValue(suppliersRef, (snapshot) => {
      const suppliersData = [];
      snapshot.forEach((childSnapshot) => {
        suppliersData.push({
          id: childSnapshot.key,
          ...childSnapshot.val(),
          outstanding: 0, // Initialize outstanding amount
        });
      });

      // Fetch invoices for each supplier to calculate outstanding amount
      const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
      onValue(invoicesRef, (invoiceSnapshot) => {
        const invoices = [];
        invoiceSnapshot.forEach((childSnapshot) => {
          const invoice = {
            id: childSnapshot.key,
            ...childSnapshot.val(),
            supplierDetails: childSnapshot.val().supplierDetails || {},
            summary: childSnapshot.val().summary || {
              netAmt: 0,
              totalPaid: 0,
              outstanding: 0,
            },
          };
          const netAmt = Number(invoice.summary.netAmt) || 0;
          const totalPaid = Number(invoice.summary.totalPaid) || 0;
          invoice.summary.outstanding = Math.max(0, netAmt - totalPaid);
          invoices.push(invoice);
        });

        // Calculate outstanding amount for each supplier
        const updatedSuppliers = suppliersData.map((supplier) => {
          const supplierInvoices = invoices.filter(
            (invoice) => invoice.supplierDetails.supplier === supplier.supplierName
          );
          const totalOutstanding = supplierInvoices.reduce(
            (sum, invoice) => sum + (Number(invoice.summary.outstanding) || 0),
            0
          );
          return { ...supplier, outstanding: totalOutstanding };
        });

        setSuppliers(updatedSuppliers);
        setLoading(false);
      });
    });

    return () => {
      off(suppliersRef);
    };
  }, [currentUser]);

  const handleDelete = async (supplierId) => {
    if (!currentUser || !window.confirm('Are you sure you want to delete this supplier?')) {
      return;
    }

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const supplierRef = ref(database, `users/${encodedEmail}/suppliers/${supplierId}`);
      await remove(supplierRef);
    } catch (error) {
      console.error('Error deleting supplier:', error);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
  };

  const handleUpdateSupplier = async (updatedData) => {
    if (!currentUser || !editingSupplier) return;

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const supplierRef = ref(database, `users/${encodedEmail}/suppliers/${editingSupplier.id}`);
      await update(supplierRef, updatedData);
      setEditingSupplier(null);
    } catch (error) {
      console.error('Error updating supplier:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingSupplier(null);
  };

  const handleActiveChange = async (supplierId, newValue) => {
    if (!currentUser) return;

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const supplierRef = ref(database, `users/${encodedEmail}/suppliers/${supplierId}`);
      await update(supplierRef, { visibility: newValue === 'YES' });
    } catch (error) {
      console.error('Error updating active status:', error);
    }
  };

  const handlePayableClick = (supplier) => {
    setSelectedSupplier(supplier);
    setShowPayableForm(true);
  };

  const handlePayableFormCancel = () => {
    setShowPayableForm(false);
    setSelectedSupplier(null);
  };

  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(
      suppliers.map((supplier, index) => ({
        'S.No': index + 1,
        'Supplier Name': supplier.supplierName || 'N/A',
        'Group': supplier.under || 'N/A',
        'Address': supplier.address || 'N/A',
        'City': supplier.city || 'N/A',
        'Mobile No': supplier.mobileNo || 'N/A',
        'GSTIN': supplier.gstTinNo || 'N/A',
        'Outstanding': `₹${Number(supplier.outstanding || 0).toFixed(2)}`,
        'Active': supplier.visibility ? 'YES' : 'NO',
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Suppliers');
    XLSX.writeFile(wb, 'Suppliers_Data.xlsx');
  };

  const filteredSuppliers = suppliers.filter(
    (supplier) =>
      supplier.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.under?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.mobileNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.gstTinNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (supplier.outstanding?.toString() || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    addButton: addButtonStyle,
    exportButton: {
      ...addButtonStyle,
      backgroundColor: '#4caf50',
      marginRight: '1rem',
    },
    searchStyle: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '0.95rem',
      width: '200px',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
   
      textAlign: 'center',
      width: '60px',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    cashButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#f59e0b',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
      transform: 'translateY(0)',
    },
    activeSelectStyle: {
      padding: '0.25rem',
      borderRadius: '4px',
      border: '1px solid #d1d5db',
      fontSize: '0.9rem',
      cursor: 'pointer',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#718096', fontSize: '1.1rem' }}>
          <h2>Please login to access the Supplier Entries</h2>
        </div>
      </div>
    );
  }

  if (editingSupplier) {
    return <SupplierForm supplier={editingSupplier} onSuccess={handleUpdateSupplier} onCancel={handleCancelEdit} />;
  }

  if (showPayableForm && selectedSupplier) {
    return (
      <PayableForm
        transactionToEdit={null}
        onSuccess={() => setShowPayableForm(false)}
        onCancel={handlePayableFormCancel}
        initialSupplier={selectedSupplier.supplierName}
      />
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Supplier Entries</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={onAddSupplier}
            style={styles.addButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
            }}
          >
            + Add Supplier Entry
          </button>
          <button
            onClick={exportToCSV}
            style={styles.exportButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(76, 175, 80, 0.3)';
            }}
          >
            Export CSV
          </button>
          <input
            type="text"
            placeholder="Search By Supplier Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchStyle}
          />
        </div>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#718096', fontSize: '1.1rem' }}>
            <p>⏳ Loading suppliers...</p>
          </div>
        ) : filteredSuppliers.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>S.No</th>
                <th style={styles.tableHeader}>Supplier Name</th>
                <th style={styles.tableHeader}>Group</th>
                <th style={styles.tableHeader}>Address</th>
                <th style={styles.tableHeader}>City</th>
                <th style={styles.tableHeader}>Mobile No</th>
                <th style={styles.tableHeader}>GSTIN</th>
                <th style={styles.tableHeader}>Outstanding</th>
                <th style={styles.tableHeader}>Active</th>
                <th style={styles.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((supplier, index) => (
                <tr
                  key={supplier.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <td style={{ ...styles.tableCell, ...styles.srNoCell }}>{index + 1}</td>
                  <td style={styles.tableCell}>{supplier.supplierName || 'N/A'}</td>
                  <td style={styles.tableCell}>{supplier.under || 'N/A'}</td>
                  <td style={styles.tableCell}>{supplier.address || 'N/A'}</td>
                  <td style={styles.tableCell}>{supplier.city || 'N/A'}</td>
                  <td style={styles.tableCell}>{supplier.mobileNo || 'N/A'}</td>
                  <td style={styles.tableCell}>{supplier.gstTinNo || 'N/A'}</td>
                  <td style={styles.tableCell}>
                    <strong
                      style={{
                        color: Number(supplier.outstanding || 0) <= 0.01 ? '#10b981' : '#ef4444',
                      }}
                    >
                      ₹{Number(supplier.outstanding || 0).toFixed(2)}
                    </strong>
                  </td>
                  <td style={styles.tableCell}>
                    <select
                      value={supplier.visibility ? 'YES' : 'NO'}
                      onChange={(e) => handleActiveChange(supplier.id, e.target.value)}
                      style={styles.activeSelectStyle}
                    >
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                    </select>
                  </td>
                  <td style={{ ...styles.tableCell, ...styles.actionCell }}>
                    <button
                      onClick={() => handleEdit(supplier)}
                      style={styles.editButton}
                      title="Edit Supplier"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(supplier.id)}
                      style={styles.deleteButton}
                      title="Delete Supplier"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                      }}
                    >
                      🗑️
                    </button>
                    <button
                      onClick={() => handlePayableClick(supplier)}
                      style={styles.cashButton}
                      title="View Payable"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
                      }}
                    >
                      💰
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={styles.noData}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>📁</div>
            <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Suppliers Found</h3>
            <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first supplier!</p>
            <button
              onClick={onAddSupplier}
              style={{
                ...styles.addButton,
                fontSize: '1rem',
                padding: '1rem 2rem',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              + Add Your First Supplier
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierTable;